"use client";

import Link from "next/link";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { config } from "@/shared/utils/config";
import { cardBase } from "@/shared/styles/card";
import { ArrowRight, Sparkles } from "lucide-react";

interface HeroProps {
  title?: string;
  subtitle?: string;
}

function TypingText({
  texts,
  speed = 50,
  pauseBetween = 2000,
  className,
}: {
  texts: string[];
  speed?: number;
  pauseBetween?: number;
  className?: string;
}) {
  const [displayedText, setDisplayedText] = useState("");
  const [showCursor, setShowCursor] = useState(true);
  const [currentTextIndex, setCurrentTextIndex] = useState(0);

  useEffect(() => {
    if (texts.length === 0) return;

    let currentIndex = 0;
    let intervalId: NodeJS.Timeout | null = null;
    let timeoutId: NodeJS.Timeout | null = null;
    let isMounted = true;

    const typeText = (textIndex: number) => {
      if (!isMounted) return;

      const text = texts[textIndex];
      currentIndex = 0;
      setDisplayedText("");

      timeoutId = setTimeout(() => {
        if (!isMounted) return;

        intervalId = setInterval(() => {
          if (!isMounted) return;

          if (currentIndex < text.length) {
            setDisplayedText(text.slice(0, currentIndex + 1));
            currentIndex++;
          } else {
            // Finished typing current text
            if (intervalId) {
              clearInterval(intervalId);
              intervalId = null;
            }

            // Wait before starting next text
            setTimeout(() => {
              if (!isMounted) return;

              const nextIndex = textIndex < texts.length - 1 ? textIndex + 1 : 0;
              setCurrentTextIndex(nextIndex);
            }, pauseBetween);
          }
        }, speed);
      }, 500); // Start typing after 500ms delay
    };

    typeText(currentTextIndex);

    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [texts, speed, pauseBetween, currentTextIndex]);

  // Blinking cursor effect
  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 530);

    return () => clearInterval(cursorInterval);
  }, []);

  return (
    <span className={clsx("opacity-90 lg:text-sm xl:text-base", className)}>
      {displayedText}
      {showCursor && <span className="animate-pulse">|</span>}
    </span>
  );
}

export default function Hero(props: Readonly<HeroProps>) {
  const [heroPreviewAction, setHeroPreviewAction] = useState<"review" | "approve" | null>(null);
  const title = props.title ?? "Stop guessing. Let Lucien run the next GTM move.";
  const subtitle =
    props.subtitle ??
    "Stop turning uncertainty into more product work. Connect your site, analytics, notes, replies, calls, revenue, and competitors. Lucien prepares the rewrite, outreach, follow-up, or watchlist, runs it after approval, and learns from the result.";

  return (
    <section id="hero" data-analytics-section="hero" className="relative overflow-hidden">
      {/* Background */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10" />

      {/* Subtle spotlight */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 -top-24 h-[420px] opacity-50 blur-3xl [background:radial-gradient(ellipse_at_center,_rgba(0,71,249,0.25),_transparent_60%)]"
      />

      <div className="mx-auto w-full max-w-7xl px-4 pb-10 pt-16 sm:px-6 sm:pb-12 md:pt-36 lg:px-8 lg:pb-16 lg:pt-40">
        <div className="grid min-w-0 gap-10 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] lg:items-center">
          {/* Left: Copy */}
          <div className="min-w-0 max-w-xl space-y-4">
            {/* Headline */}
            <h1 className="max-w-full text-pretty font-sg text-[2.15rem] leading-[1.06] tracking-tight text-foreground min-[430px]:text-[2.55rem] sm:text-[3.2rem] md:text-[4rem] lg:text-[4.15rem]">
              {title}
            </h1>

            {/* Subhead */}
            <p
              className="max-w-2xl text-[0.9rem] font-normal leading-7 text-slate-700 sm:text-base sm:leading-8 lg:max-w-xl"
              dangerouslySetInnerHTML={{ __html: subtitle }}
            />

            {/* CTAs */}
            <div className="flex flex-col items-stretch gap-3 min-[460px]:flex-row min-[460px]:flex-wrap min-[460px]:items-center sm:gap-4">
              <Link
                href="/sign-up"
                className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-full bg-blue px-6 text-sm font-semibold text-white shadow-sm ring-4 ring-transparent hero-cta-animated [animation:hero-cta-ring_1.8s_ease-in-out_infinite_alternate] hover:brightness-90 active:brightness-80 min-[460px]:w-auto min-[460px]:whitespace-nowrap"
                data-cta="hero-get-started"
              >
                Start 1-week free trial
                <ArrowRight className="size-4" aria-hidden="true" />
              </Link>

              <Link
                href="/pricing"
                className="inline-flex h-11 w-full items-center justify-center rounded-full border border-slate-300 bg-white px-6 text-sm font-medium text-slate-700 hover:bg-slate-50 min-[460px]:w-auto min-[460px]:whitespace-nowrap"
                data-cta="hero-see-pricing"
              >
                See pricing
              </Link>

              <span className="basis-full text-xs leading-5 text-muted-foreground">
                Start free for one week. No card required. Plans from $39/mo.
              </span>
              <span className="inline-flex basis-full items-center gap-2 text-xs font-medium leading-5 text-slate-600">
                <span className="size-1.5 rounded-full bg-blue" aria-hidden="true" />
                Small validation cohort now open.
              </span>
            </div>
          </div>

          {/* Right: Graphic */}
          <div className="relative min-w-0">
            <div className={`${cardBase} w-full max-w-lg overflow-hidden p-4 sm:p-5 lg:ml-auto`}>
              <div className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50/70 px-3.5 py-3">
                <div>
                  <p className="text-xs font-medium uppercase tracking-[0.14em] text-blue">
                    Lucien is watching
                  </p>
                  <p className="mt-0.5 text-sm font-semibold text-slate-950">Example buyer read</p>
                </div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-blue/10 px-2.5 py-1 text-xs font-medium text-blue ring-1 ring-blue/15">
                  <span className="size-1.5 rounded-full bg-blue" aria-hidden="true" />
                  Live
                </span>
              </div>

              <div className="mt-3 space-y-2.5">
                <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-xs font-medium uppercase tracking-[0.12em] text-slate-500">
                      Weak claim found
                    </div>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">
                      5 replies
                    </span>
                  </div>
                  <div className="mt-1 text-base font-semibold text-slate-900">
                    You sell automation. Buyers talk about handoff risk.
                  </div>
                  <div className="mt-1 text-sm text-slate-500">
                    Lucien maps buyer language against the page and competitor claims.
                  </div>
                </div>

                <div className="grid gap-2.5 sm:grid-cols-2">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-3.5">
                    <div className="text-xs font-medium uppercase tracking-[0.12em] text-slate-500">
                      Buyers found
                    </div>
                    <div className="mt-1 text-sm font-semibold leading-5 text-slate-900">
                      12 operators likely to feel the pain.
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-3.5">
                    <div className="text-xs font-medium uppercase tracking-[0.12em] text-slate-500">
                      Action drafted
                    </div>
                    <div className="mt-1 text-sm font-semibold leading-5 text-slate-900">
                      Outreach asks who owns the risk.
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-blue/15 bg-blue/[0.04] p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-xs font-medium uppercase tracking-[0.12em] text-blue">
                      Ready to execute
                    </p>
                    <span
                      className={clsx(
                        "rounded-full px-2.5 py-1 text-xs font-medium ring-1",
                        heroPreviewAction === "approve"
                          ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                          : "bg-amber-50 text-amber-700 ring-amber-200"
                      )}
                    >
                      {heroPreviewAction === "approve" ? "Approved" : "Needs approval"}
                    </span>
                  </div>
                  <p className="mt-2 text-base font-semibold text-slate-950">
                    I rewrote the hero and prepared outreach to 12 leads.
                  </p>
                  <p className="mt-1 text-sm leading-6 text-slate-600">
                    Approve it and Lucien updates the draft, sends the test, and reports what
                    changed.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setHeroPreviewAction("review")}
                      className="inline-flex h-8 items-center justify-center rounded-full border border-amber-300 bg-amber-50 px-3 text-xs font-semibold text-amber-700"
                    >
                      Review
                    </button>
                    <button
                      type="button"
                      onClick={() => setHeroPreviewAction("approve")}
                      className="inline-flex h-8 items-center justify-center rounded-full bg-emerald-600 px-3 text-xs font-semibold text-white"
                    >
                      Approve
                    </button>
                  </div>
                  {heroPreviewAction ? (
                    <p className="mt-3 rounded-xl border border-blue/10 bg-white/70 px-3 py-2 text-xs font-medium leading-5 text-slate-600">
                      {heroPreviewAction === "approve"
                        ? "Approved in this preview: Lucien would send the test and track replies."
                        : "Review mode: Lucien would show the rewrite, leads, and signal first."}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Floating search bar */}
            <div
              className="pointer-events-none absolute -left-8 -top-7 hidden w-[96%] rounded-2xl border border-white/20 bg-gradient-to-r from-[#2148D8] to-[#245CFF] px-4 py-3 text-white backdrop-blur lg:block"
              style={{
                boxShadow: "0 0 0 2px rgba(255,255,255,0.14), 0 0 38px rgba(0,71,249,0.38)",
                animation: "hero-input-glow 2400ms ease-in-out infinite",
              }}
            >
              <div className="flex items-center gap-3 text-base">
                <span
                  aria-hidden
                  className="inline-flex size-7 items-center justify-center rounded-md border border-orange-100/50 text-sm leading-none shrink-0"
                >
                  <Sparkles className="size-[18px] aspect-square opacity-90 text-orange-300" />
                </span>
                <TypingText
                  texts={[
                    "I found 12 buyers to test the handoff-risk message.",
                    "I rewrote the page around the pain buyers repeated.",
                    "I drafted the budget test. Approve it and I will run it.",
                    "I will track replies and update the next move.",
                  ]}
                  speed={80}
                  pauseBetween={2000}
                  className="font-sg text-sm"
                />
              </div>
            </div>
            <style>{`@keyframes hero-input-glow{0%{box-shadow:0 0 0 2px rgba(255,255,255,.12),0 0 16px rgba(0,71,249,.25)}50%{box-shadow:0 0 0 2px rgba(255,255,255,.18),0 0 36px rgba(0,71,249,.45)}100%{box-shadow:0 0 0 2px rgba(255,255,255,.12),0 0 16px rgba(0,71,249,.25)}}`}</style>
          </div>
        </div>
      </div>
    </section>
  );
}
